import {
    isPossiblePhoneNumber,
    parsePhoneNumberFromString,
} from "libphonenumber-js"
import { capitalize } from "radash"

import { CURRENCY_LOCALE } from "~config/constants"
import COPY from "~config/copy-constants"
// Function that takes a phone number in the format +19999999999 and returns it as +1 (999) 999-9999

// Will return the original string if it is not a valid phone number
export function formatPhoneNumber(phoneNumber: string | null | undefined) {
    if (!phoneNumber) return ""

    const parsedPhone = parsePhoneNumberFromString(phoneNumber)

    return parsedPhone?.formatInternational() ?? phoneNumber
}

// Function that takes a phone number and returns it in international format without spaces
// Will return the original string if it is not a valid phone number
export function parsePhoneNumberToE164(phoneNumber: string | null | undefined) {
    if (!phoneNumber) return ""

    // First try parsing without country code
    let parsedPhone = parsePhoneNumberFromString(phoneNumber)

    // If that fails, try with US country code
    if (!parsedPhone || !parsedPhone.isValid()) {
        parsedPhone = parsePhoneNumberFromString(phoneNumber, "US")
    }

    // If still no valid phone, try cleaning the number and parsing again
    if (!parsedPhone || !parsedPhone.isValid()) {
        const cleanedNumber = phoneNumber.replace(/[^\d+]/g, "")
        if (cleanedNumber !== phoneNumber) {
            parsedPhone = parsePhoneNumberFromString(cleanedNumber, "US")
        }
    }

    return parsedPhone?.number ?? phoneNumber
}

export function formatPrice(
    price: number | null | undefined,
    hideZeroCents = false
): string {
    if (typeof price !== "number") return "N/A"

    const negativeNotation = price < 0 ? "-" : ""
    const valueString = centsToDollars(Math.abs(price)).toLocaleString(
        undefined,
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    )

    const formattedPrice = `${negativeNotation}$${valueString}`

    return hideZeroCents ? formattedPrice.replace(".00", "") : formattedPrice
}

function toCompactNumber(number: number): string {
    return Intl.NumberFormat(CURRENCY_LOCALE.US, {
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(number)
}

export function toCompactPrice(number: number): string {
    return `$${toCompactNumber(number)}`
}

export function formatPercentage(percent: number | null | undefined) {
    if (typeof percent !== "number") return COPY.NA

    return `${percent}%`
}

export function centsToDollars(cents?: number | null): number {
    if (!cents) return 0
    return cents / 100
}

export function getCleanEnum(dirty: string | null | undefined): string {
    return dirty
        ? dirty
              .replace(/_/g, " ")
              .split(" ")
              .map((str) => str.charAt(0) + str.slice(1).toLowerCase())
              .join(" ")
        : ""
}

export function makeStringFromArray({
    strings,
    delimitter = " ",
}: {
    strings: (string | null | undefined)[]
    delimitter?: string
}) {
    return strings
        .filter((stringValue) => Boolean(stringValue))
        .join(delimitter)
}

export function getFullName(
    user?: {
        first_name?: string | null
        last_name?: string | null
    } | null
): string {
    if (user?.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`
    }

    return user?.first_name ?? user?.last_name ?? "N/A"
}

export function validatePhoneNumber({
    phone,
    isEmptyValid = true,
}: {
    phone: string | null | undefined
    isEmptyValid?: boolean
}): boolean {
    if (!phone) return isEmptyValid

    const hasCountryCode = phone.startsWith("+")

    return hasCountryCode && isPossiblePhoneNumber(phone)
}

export function startsWithAny(value: string, values: string[]) {
    return values.some((valueToCheck) => value.startsWith(valueToCheck))
}

export function getStatusText(status: string) {
    return capitalize(status.toLowerCase().replace("_", " "))
}

export function startCase(str: string): string {
    if (!str) return ""

    return str
        .replace(/([a-z])([A-Z])/g, "$1 $2") // Insert space before capital letters
        .replace(/[_-]/g, " ") // Replace underscores and hyphens with spaces
        .replace(/\s+/g, " ") // Replace multiple spaces with single space
        .trim()
        .split(" ")
        .map((word) => {
            if (!word) return ""
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        })
        .join(" ")
}
